import { ArrowUpOutlined, CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AGENT_MESSAGE_MAX_LENGTH } from '@ctd/agent-contract';
import type { ConversationDetail } from '@ctd/agent-contract';
import { useAuthStore } from '~contexts/mobx';
import { useAgentChat } from '~contexts/agent-chat';
import { HistoryMenu } from './components/history-menu';
import { TurnView } from './components/turn-view';
import { useAgentStream } from './hooks/use-agent-stream';
import style from './style.module.scss';

/** Pre-defined prompts: clicking one copies `query` into the input. */
interface Suggestion {
    title: string;
    subtitle: string;
    query: string;
}

const SUGGESTIONS: Suggestion[] = [
    {
        title: 'Weekly client report',
        subtitle: 'Circle K + Couche-Tard Québec',
        query:
            'Generate the Circle K + Couche-Tard Québec client metrics report for this week.',
    },
    {
        title: 'Active users by province',
        subtitle: 'Last 30 days',
        query: 'How many active users by province over the last 30 days?',
    },
    {
        title: 'New users, week over week',
        subtitle: 'By business unit',
        query:
            'New users last week by business unit, compared with the week before.',
    },
];

const STICK_TO_BOTTOM_WITHIN_PX = 48;

const earlierTurnsNote = (count: number): string =>
    `${count} earlier question${count === 1 ? '' : 's'} not shown`;

/** First name from the Okta id-token claims, or null when unavailable. */
function firstNameOf(name: string | undefined): string | null {
    const trimmed = name?.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed.split(/\s+/)[0];
}

/**
 * Global agent chat overlay (managementAccess-gated, like AskAIButton). Mounted
 * once at the home-page level; pops up over the current page content with a
 * bottom-up gradient. The idle state shows a greeting + the suggestion + the ask
 * bar; once a question is sent the conversation thread (one block per question:
 * activity lines / cards / answer) replaces the welcome. Closing the overlay stops
 * a turn in flight.
 */
export const AgentChatDrawer = observer(() => {
    const authStore = useAuthStore();
    const { isOpen, close } = useAgentChat();
    const {
        thread,
        isStreaming,
        ask,
        stop,
        newConversation,
        loadConversation,
    } = useAgentStream();
    const [question, setQuestion] = useState('');
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const threadRef = useRef<HTMLDivElement>(null);
    const followsThread = useRef(true);

    useEffect(() => {
        if (!isOpen) {
            stop();
            setQuestion('');
            setIsHistoryOpen(false);
        }
    }, [isOpen, stop]);

    useEffect(() => {
        if (isOpen) {
            followsThread.current = true;
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // The input is disabled while a turn runs, which takes its focus away; it gets it back
    // unless the reader has put the focus somewhere else meanwhile.
    useEffect(() => {
        const active = document.activeElement;
        if (isOpen && !isStreaming && (!active || active === document.body)) {
            inputRef.current?.focus();
        }
    }, [isOpen, isStreaming]);

    // Esc closes the history menu first, then the overlay.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }
            if (isHistoryOpen) {
                setIsHistoryOpen(false);
            } else {
                close();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, isHistoryOpen, close]);

    // The newest block stays in view unless the reader scrolled up; opening shows it again.
    useEffect(() => {
        const list = threadRef.current;
        if (list && followsThread.current) {
            list.scrollTop = list.scrollHeight;
        }
    }, [thread, isOpen]);

    const onAnswer = useCallback(
        (text: string) => {
            followsThread.current = true;
            ask(text);
        },
        [ask]
    );

    if (!authStore.hasManagementAccess || !isOpen) {
        return null;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        followsThread.current = true;
        ask(question);
        setQuestion('');
    };

    const onThreadScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const list = event.currentTarget;
        followsThread.current =
            list.scrollHeight - list.scrollTop - list.clientHeight <
            STICK_TO_BOTTOM_WITHIN_PX;
    };

    const onSelectConversation = (detail: ConversationDetail) => {
        followsThread.current = true;
        loadConversation(detail);
        inputRef.current?.focus();
    };

    const onNewConversation = () => {
        newConversation();
        inputRef.current?.focus();
    };

    const name = firstNameOf(authStore.claims?.name);
    const greeting = name ? `Hi ${name}` : 'Hi';
    const isIdle = thread.turns.length === 0;

    return (
        <div className={style.overlay} role="dialog" aria-modal="true">
            <div className={style.backdrop} aria-hidden="true" />
            <div className={style.tools}>
                <button
                    type="button"
                    className={style.close}
                    aria-label="Close assistant"
                    onClick={close}
                >
                    <CloseOutlined />
                </button>
                <HistoryMenu
                    isOpen={isHistoryOpen}
                    onOpenChange={setIsHistoryOpen}
                    currentConversationId={thread.conversationId}
                    onSelectConversation={onSelectConversation}
                    onNewConversation={onNewConversation}
                />
            </div>

            <div className={style.console}>
                {isIdle ? (
                    <div className={style.welcome}>
                        <h2 className={style.greeting}>
                            {greeting} <span aria-hidden="true">👋</span>
                        </h2>
                        <p className={style.subtitle}>
                            Ask about your data. I can pull metrics and generate
                            PDF or Excel reports
                        </p>
                        <div className={style.suggestions}>
                            {SUGGESTIONS.map((suggestion) => (
                                <button
                                    type="button"
                                    key={suggestion.title}
                                    className={style.suggestion}
                                    onClick={() =>
                                        setQuestion(suggestion.query)
                                    }
                                >
                                    <span className={style.suggestion_text}>
                                        <span
                                            className={style.suggestion_title}
                                        >
                                            {suggestion.title}
                                        </span>
                                        <span
                                            className={
                                                style.suggestion_subtitle
                                            }
                                        >
                                            {suggestion.subtitle}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div
                        className={style.thread}
                        ref={threadRef}
                        onScroll={onThreadScroll}
                    >
                        {thread.earlierTurns > 0 && (
                            <p className={style.earlier_turns}>
                                {earlierTurnsNote(thread.earlierTurns)}
                            </p>
                        )}
                        {thread.turns.map((turn, index) => {
                            const last = index === thread.turns.length - 1;
                            return (
                                <TurnView
                                    key={index}
                                    turn={turn}
                                    answeredWith={
                                        thread.turns[index + 1]?.question
                                    }
                                    onAnswer={
                                        last && !isStreaming
                                            ? onAnswer
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </div>
                )}

                <form className={style.bar} onSubmit={onSubmit}>
                    <input
                        ref={inputRef}
                        className={style.input}
                        type="text"
                        value={question}
                        placeholder="Ask Couche-Tard AI about your data..."
                        onChange={(event) => setQuestion(event.target.value)}
                        maxLength={AGENT_MESSAGE_MAX_LENGTH}
                        disabled={isStreaming}
                    />
                    <button
                        type="submit"
                        className={style.send}
                        disabled={isStreaming || !question.trim()}
                        aria-label="Send"
                    >
                        <ArrowUpOutlined />
                    </button>
                </form>
            </div>
        </div>
    );
});
